/* eslint-disable @angular-eslint/no-input-rename */
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroupDirective,
  FormsModule,
  NG_VALIDATORS,
  NgControl,
  NgForm,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import {
  AsYouType,
  CountryCode as CC,
  E164Number,
  getExampleNumber,
  NationalNumber,
  parsePhoneNumberFromString,
  PhoneNumber,
} from 'libphonenumber-js/min';
import { Subject } from 'rxjs';
import { CountryCode, Examples } from './data/country-code';
import { Country } from './model/country.model';
import { PhoneNumberFormat } from './model/phone-number-format.model';
import { phoneNumberValidator } from './ngx-mat-intl-tel-input.validator';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ngx-mat-intl-tel-input',
  templateUrl: './ngx-mat-intl-tel-input.component.html',
  styleUrls: ['./ngx-mat-intl-tel-input.component.css'],
  imports: [
    FormsModule,
    MatInputModule,
    MatMenuModule,
    MatButtonModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  providers: [
    CountryCode,
    { provide: MatFormFieldControl, useExisting: NgxMatIntlTelInputComponent },
    {
      provide: NG_VALIDATORS,
      useValue: phoneNumberValidator,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[id]': 'id',
    '[class.ngx-floating]': 'shouldLabelFloat',
    '(click)': 'onContainerClick($event)',
  },
})
export class NgxMatIntlTelInputComponent
  implements OnInit, MatFormFieldControl<string | null>, ControlValueAccessor
{
  static nextId = 0;

  // Signal Inputs
  readonly preferredCountries = input<string[]>([]);
  readonly enablePlaceholder = input<boolean, unknown>(true, { transform: booleanAttribute });
  readonly inputPlaceholder = input<string>('');
  readonly cssClass = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly onlyCountries = input<string[]>([]);
  readonly errorStateMatcher = input<ErrorStateMatcher | undefined>(undefined);
  readonly enableSearch = input<boolean, unknown>(false, { transform: booleanAttribute });
  readonly searchPlaceholder = input<string>('Search ...');
  readonly describedBy = input<string>('');
  readonly format = input<PhoneNumberFormat>('default');

  // MatFormFieldControl Inputs
  readonly placeholderInput = input<string>('', { alias: 'placeholder' });
  readonly requiredInput = input<boolean, unknown>(false, { alias: 'required', transform: booleanAttribute });
  readonly disabledInput = input<boolean, unknown>(false, { alias: 'disabled', transform: booleanAttribute });

  // Signal Outputs
  readonly countryChanged = output<Country>();

  // View Queries
  readonly matMenu = viewChild(MatMenu);

  // Injections
  private readonly countryCodeData = inject(CountryCode);
  private readonly fm = inject(FocusMonitor);
  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly defaultErrorStateMatcher =
    inject(ErrorStateMatcher, { optional: true }) ?? new ErrorStateMatcher();
  private readonly parentForm = inject(NgForm, { optional: true });
  private readonly parentFormGroup = inject(FormGroupDirective, { optional: true });
  public readonly ngControl = inject(NgControl, { optional: true, self: true });

  // Internal Reactive State
  readonly stateChanges = new Subject<void>();
  readonly id = `ngx-mat-intl-tel-input-${NgxMatIntlTelInputComponent.nextId++}`;
  readonly controlType = 'ngx-mat-intl-tel-input';

  readonly phoneNumber = signal<NationalNumber | string>('');
  readonly searchCriteria = signal<string>('');
  readonly selectedCountry = signal<Country | undefined>(undefined);
  private readonly extraPreferredCountry = signal<Country | undefined>(undefined);
  private readonly focusedState = signal<boolean>(false);
  private readonly disabledCva = signal<boolean>(false);
  private readonly _userAriaDescribedBy = signal<string>('');

  private numberInstance: PhoneNumber | undefined;
  private _value: E164Number | string | null = null;
  private previousFormattedNumber: string | undefined;

  // CVA callbacks
  onTouched: () => void = () => {
    // CVA onTouched default implementation
  };
  propagateChange: (value: unknown) => void = (_: unknown) => {
    // CVA propagateChange default implementation
  };

  // Computed Properties
  readonly rawCountries = computed<Country[]>(() => {
    const enablePh = this.enablePlaceholder();
    return this.countryCodeData.allCountries.map((c) => {
      const iso2 = c[1].toString();
      const isoUpper = iso2.toUpperCase() as CC;
      return {
        name: c[0].toString(),
        iso2,
        dialCode: c[2].toString(),
        priority: +c[3] || 0,
        areaCodes: (c[4] as string[]) || undefined,
        flagClass: isoUpper,
        placeHolder: enablePh ? NgxMatIntlTelInputComponent.getPhoneNumberPlaceHolder(isoUpper) : '',
      };
    });
  });

  readonly allCountries = computed<Country[]>(() => {
    const only = this.onlyCountries();
    const raw = this.rawCountries();
    if (only.length === 0) {
      return raw;
    }
    return raw.filter((c) => only.includes(c.iso2));
  });

  readonly preferredCountriesInDropDown = computed<Country[]>(() => {
    const preferred = this.preferredCountries();
    const all = this.allCountries();
    const extra = this.extraPreferredCountry();
    const list: Country[] = [];

    if (preferred.length > 0) {
      for (const iso2 of preferred) {
        const found = all.find((c) => c.iso2 === iso2);
        if (found) {
          list.push(found);
        }
      }
    }

    if (extra && !preferred.includes(extra.iso2) && !list.some((c) => c.iso2 === extra.iso2)) {
      list.push(extra);
    }

    return list;
  });

  readonly filteredCountries = computed<Country[]>(() => {
    const criteria = this.searchCriteria().trim().toLowerCase();
    const countries = this.allCountries();
    if (!criteria) {
      return countries;
    }
    return countries.filter((c) => `${c.name}+${c.dialCode}`.toLowerCase().includes(criteria));
  });

  readonly ariaDescribedBy = computed(() => {
    return [this.describedBy(), this._userAriaDescribedBy()].filter(Boolean).join(' ');
  });

  // MatFormFieldControl Contract Getters & Setters
  get value(): string | null {
    return this._value;
  }

  set value(val: string | null) {
    this._value = val;
    this.stateChanges.next();
  }

  get placeholder(): string {
    return this.placeholderInput();
  }

  get focused(): boolean {
    return this.focusedState();
  }

  get empty(): boolean {
    const current = this.phoneNumber();
    return !current || current.toString().trim() === '';
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get required(): boolean {
    return this.requiredInput();
  }

  get disabled(): boolean {
    return this.disabledInput() || this.disabledCva();
  }

  get userAriaDescribedBy(): string {
    return this._userAriaDescribedBy();
  }

  set userAriaDescribedBy(value: string) {
    this._userAriaDescribedBy.set(value);
  }

  get errorState(): boolean {
    const control = this.ngControl?.control;
    if (!control) {
      return false;
    }
    const matcher = this.errorStateMatcher() ?? this.defaultErrorStateMatcher;
    if (matcher) {
      return matcher.isErrorState(
        control as unknown as AbstractControl<unknown, unknown>,
        (this.parentForm ?? this.parentFormGroup) as unknown as FormGroupDirective | NgForm,
      );
    }
    const isSubmitted = !!(this.parentForm?.submitted || this.parentFormGroup?.submitted);
    return !!(control.invalid && (control.touched || control.dirty || isSubmitted));
  }

  static getPhoneNumberPlaceHolder(countryISOCode: CC): string | undefined {
    const result = getExampleNumber(countryISOCode, Examples);
    return result ? result.number.toString() : undefined;
  }

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.fm
      .monitor(this.elRef, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((origin) => {
        if (this.focusedState() && !origin) {
          this.onTouched();
        }
        this.focusedState.set(!!origin);
        this.stateChanges.next();
      });

    effect(() => {
      // Reformat phone number when format input changes
      const _fmt = this.format();
      untracked(() => {
        if (this.numberInstance) {
          this.phoneNumber.set(this.formattedPhoneNumber as NationalNumber);
          this.stateChanges.next();
        }
      });
    });
  }

  ngOnInit(): void {
    if (!this.selectedCountry()) {
      const preferred = this.preferredCountriesInDropDown();
      const all = this.allCountries();
      const initial = preferred[0] ?? all[0];
      if (initial) {
        this.selectedCountry.set(initial);
        this.countryChanged.emit(initial);
      }
    }
  }

  public onInputModelChange(val: string): void {
    this.phoneNumber.set(val);
    this.onPhoneNumberChange();
  }

  public onPhoneNumberChange(): void {
    const currentCountry = this.selectedCountry();
    const currentPhone = this.phoneNumber()?.toString() || '';

    try {
      this.numberInstance = parsePhoneNumberFromString(
        currentPhone,
        currentCountry?.iso2.toUpperCase() as CC,
      );
      this.formatAsYouTypeIfEnabled();
      this.value = this.numberInstance?.number ?? currentPhone;

      if (this.numberInstance && this.numberInstance.isValid()) {
        const formatted = this.formattedPhoneNumber;
        if (this.phoneNumber() !== formatted) {
          this.phoneNumber.set(formatted as NationalNumber);
        }
        if (currentCountry?.iso2 !== this.numberInstance.country && !!this.numberInstance.country) {
          const newCountry = this.getCountry(this.numberInstance.country);
          this.selectedCountry.set(newCountry);
          this.countryChanged.emit(newCountry);
        }
      }
    } catch {
      this.value = currentPhone;
    }

    this.propagateChange(this.value);
    this.stateChanges.next();
  }

  public onCountrySelect(country: Country): void {
    if (this.phoneNumber()) {
      this.phoneNumber.set((this.numberInstance?.nationalNumber as NationalNumber) ?? this.phoneNumber());
    }
    this.selectedCountry.set(country);
    this.countryChanged.emit(country);
    this.onPhoneNumberChange();
  }

  public getCountry(code: string): Country {
    return (
      this.allCountries().find((c) => c.iso2 === code.toLowerCase()) || {
        name: 'UN',
        iso2: 'UN',
        dialCode: '',
        priority: 0,
        areaCodes: undefined,
        flagClass: 'UN',
        placeHolder: '',
      }
    );
  }

  public onInputKeyPress(event: KeyboardEvent): void {
    const pattern = /[0-9+\- ]/;
    if (!pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledCva.set(isDisabled);
    this.stateChanges.next();
  }

  writeValue(value: string | null): void {
    if (value) {
      this.numberInstance = parsePhoneNumberFromString(value);
      if (this.numberInstance) {
        const countryCode = this.numberInstance.country;
        this.phoneNumber.set(this.formattedPhoneNumber as NationalNumber);
        if (countryCode) {
          const country = this.getCountry(countryCode);
          this.selectedCountry.set(country);
          if (country.dialCode && !this.preferredCountries().includes(country.iso2)) {
            this.extraPreferredCountry.set(country);
          }
          this.countryChanged.emit(country);
        }
      } else {
        this.phoneNumber.set(value);
      }
    } else {
      this.phoneNumber.set('');
      this.numberInstance = undefined;
    }

    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this._userAriaDescribedBy.set(ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName?.toLowerCase() !== 'input') {
      const inputEl = this.elRef.nativeElement.querySelector('input');
      if (inputEl) {
        inputEl.focus();
      }
    }
  }

  reset(): void {
    this.phoneNumber.set('');
    this.numberInstance = undefined;
    this.propagateChange(null);
    this.stateChanges.next();
  }

  private get formattedPhoneNumber(): string {
    if (!this.numberInstance) {
      return this.phoneNumber()?.toString() || '';
    }
    switch (this.format()) {
      case 'national':
        return this.numberInstance.formatNational();
      case 'international':
        return this.numberInstance.formatInternational();
      default:
        return this.numberInstance.nationalNumber.toString();
    }
  }

  private formatAsYouTypeIfEnabled(): void {
    if (this.format() === 'default') {
      return;
    }
    const asYouType = new AsYouType(this.selectedCountry()?.iso2.toUpperCase() as CC);
    const phone = this.phoneNumber()?.toString() || '';
    if (phone.startsWith(this.previousFormattedNumber || '')) {
      this.phoneNumber.set(asYouType.input(phone) as NationalNumber);
    }
    this.previousFormattedNumber = this.phoneNumber()?.toString();
  }
}
